import { useEffect } from 'react';
import useInitialHydration from './useInitialHydration';
import usePaginatedResults from './usePaginatedResults';

const useRateResultsFetcher = <A extends unknown[]>({
  fetchMore,
  getFetchMoreArgs = undefined,
  hasMore,
  hydrate,
  isBlocked = false,
  isLoadingData,
  runFetch,
}: {
  fetchMore: (...args: A) => void;
  getFetchMoreArgs?: () => A;
  hasMore: boolean;
  hydrate: () => void;
  isBlocked?: boolean;
  isLoadingData: boolean;
  runFetch: () => void;
}) => {
  const isHydrated = useInitialHydration(hydrate);
  const isReady = isHydrated && !isBlocked;
  const { fetchMoreResults, isLoading, markFetchedOnce } = usePaginatedResults({
    fetchMore,
    getFetchMoreArgs,
    hasMore,
    isLoadingData,
    isReady,
  });

  useEffect(() => {
    if (!isReady) return undefined;

    let isMounted = true;

    Promise.resolve(runFetch()).finally(() => {
      if (isMounted) {
        markFetchedOnce();
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isReady, markFetchedOnce, runFetch]);

  return { fetchMoreResults, isHydrated, isLoading };
};

export default useRateResultsFetcher;
